import React, { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import type { IngestionDraft } from "./types";

interface UploadIngestionTabProps {
  onCapture: (draft: IngestionDraft) => void;
}

const ACCEPTED_TYPES = ".pdf,.docx,.doc,.txt,.csv";
const ACCEPTED_EXTENSIONS = ["pdf", "docx", "doc", "txt", "csv"];
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isValidFile = (file: File): { valid: boolean; error?: string } => {
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (!ext || !ACCEPTED_EXTENSIONS.includes(ext)) {
    return { valid: false, error: `Invalid file type. Allowed: ${ACCEPTED_EXTENSIONS.join(", ")}` };
  }
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: `File too large. Max size: 20MB` };
  }
  return { valid: true };
};

const UploadIngestionTab = ({ onCapture }: UploadIngestionTabProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputId = "admin-file-upload";
  const [isDragOver, setIsDragOver] = useState(false);

  const processFile = (file: File) => {
    const validation = isValidFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    const draft: IngestionDraft = {
      source: {
        source_type: "upload",
        file_name: file.name,
        file_size: file.size,
        timestamp: new Date().toISOString(),
      },
      raw_content: `File: ${file.name} (${formatFileSize(file.size)})`,
      status: "captured",
      data_type: null,
      structured_extraction: null,
      ai_notes: null,
    };

    onCapture(draft);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        accept={ACCEPTED_TYPES}
        onChange={handleFileChange}
        className="hidden"
      />
      
      <label
        htmlFor={inputId}
        data-no-drag="true"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors block ${
          isDragOver
            ? "border-white/70 bg-white/10"
            : "border-white/30 hover:border-white/50 hover:bg-white/5"
        }`}
      >
        <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragOver ? "text-white/80" : "text-white/60"}`} />
        <p className="text-white/80 text-sm">
          {isDragOver ? "Drop file here" : "Click or drag to upload"}
        </p>
        <p className="text-white/50 text-xs mt-1">PDF, Word, TXT, or CSV (max 20MB)</p>
      </label>
    </div>
  );
};

export default UploadIngestionTab;
